import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { Avatar, Divider, Stack, TextField, Typography } from "@mui/material";
import CardTitleWithSubtitle from "../../../components/CardTitleWithSubtitle/CardTitleWithSubtitle";
import CustomCard from "../../../components/CustomCard/CustomCard";
import Layout from "../../../components/Layout/Layout";
import QueryPageTitleCard from "../../../components/QueryPageTitleCard/QueryPageTitleCard";
import { useNavigate, useParams } from "react-router-dom";
import { routesConfig } from "../../../config/routes";
import BreadcrumbCustom, {
  BreadcrumbChild,
} from "../../../components/BreadcrumbCustom/BreadcrumbCustom";
import Select, { SelectVariant } from "../../../components/Select/Select";
import SelectSubsidiaries from "../../../components/SelectSubsidiary/SelectSubsidiary";
import { useDistributor } from "../../../contexts/distributor/DistributorContext";
import CustomButton, {
  CustomButtonProps,
  CustomButtonVariant,
} from "../../../components/CustomButton/CustomButton";
import { RequestModal } from "./RequestModal";
import { Textarea } from "./Textarea";
import { useFormik } from "formik";
import {
  initialValues,
  reducer,
  schemaValidation,
  State,
} from "../constants/schema";
import { useOrderQuote } from "../../../contexts/orderQuote/OrderQuoteContext";
import {
  ModalityDTO,
  ProductDTO,
} from "../../../modules/distributors/models/DistributorListDTO";
import { useIocContext } from "../../../contexts/ioc/IocContext";
import { IOperationalService } from "../../../modules/operational/models/IOperationalService";
import { Types } from "../../../ioc/types";
import { GetContractsDTO } from "../../../modules/operational/models/GetContractsDTO";
import { IStorageService } from "../../../modules/storage/models/IStorageService";
import useDialogAlert from "../../../hooks/DialogAlert";
import AppError from "../../../utils/appError";
import { SkeletonLoading } from "./SkeletonLoading";
import { ComponentFactory } from "../constants/component-factory";
import CalendarSingle from "../../../components/CalendarSingle/CalendarSingle";
import { add, addMonths, format } from "date-fns";
import {
  generateAvailableTimes,
  getFormattedHour,
} from "../../../utils/dateFormatter";
import { applyVolumeMask } from "../../../utils";

interface Props {
  breadcrumbs: BreadcrumbChild[];
}

const CreateScheduleForm: React.FC<Props> = ({ breadcrumbs }) => {
  const [state, dispatch] = useReducer(reducer, {
    client: null,
    codeSubsidiary: null,
    contract: null,
    date: null,
    time: null,
    product: null,
    modality: null,
    quantity: null,
    note: null,
  });

  const [products, setProducts] = useState<ProductDTO[]>([]);
  const [modalities, setModalities] = useState<ModalityDTO[]>([]);
  const [contracts, setContracts] = useState<GetContractsDTO>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const [fetchContract, setFetchContract] = useState({
    customerId: "",
    subsidiaryId: "",
  });
  const [contractAndProduct, setContractAndProduct] = useState({
    productId: "",
    contractId: "",
  });
  const [payload, setPayload] = useState<typeof initialValues | undefined>(
    undefined
  );
  const [productMeasurementUnit, setProductMeasurementUnit] =
    useState<string>("");

  const navigate = useNavigate();

  const { id } = useParams();
  const { snackbar } = useDialogAlert();
  const { serviceContainer } = useIocContext();
  const { getProducts, getModalities } = useOrderQuote();
  const { distributors, subsidiaries } = useDistributor();

  const { getFieldOptions } = ComponentFactory;

  const operationalService = serviceContainer.get<IOperationalService>(
    Types.Operational.IOperationalService
  );
  const storageService = serviceContainer.get<IStorageService>(
    Types.Storage.IStorageService
  );

  const { hash } = window.location;
  const slug = hash.split("#")[1];

  const isEditing = Boolean(id);

  const readyToFetch =
    isEditing && Boolean(distributors.length) && Boolean(subsidiaries.length);

  const isDischarge = useMemo(
    () => slug.includes(routesConfig.STORAGE().CREATE_SCHEDULING),
    [slug]
  );

  const setEndpointBySlug = useMemo(() => {
    return isDischarge ? "unload" : "retrieval";
  }, [isDischarge]);

  const buttonTitle = useMemo(
    () => `Voltar para pedidos de ${isDischarge ? "descarga" : "entrega"}`,
    [isDischarge]
  );

  const calendarDateByEditing: [Date, Date] | [null, null] = useMemo(() => {
    if (isEditing) {
      const dateToSet = state.date ? new Date(state.date) : new Date();
      return [
        isDischarge ? new Date(dateToSet) : add(dateToSet, { days: 1 }),
        new Date(),
      ];
    }

    return [null, null];
  }, [isEditing, isDischarge, state.date]);

  useEffect(() => {
    if (isEditing && !state.time) {
      const today = new Date();
      const timeToSet = getFormattedHour(
        state.date ? new Date(state.date) : today
      );

      dispatch({ type: "set-time", value: timeToSet });
      setFieldValue("time", timeToSet);
    }
  }, [isEditing, state.date]);

  const { setFieldValue, setValues, errors, handleSubmit } = useFormik({
    initialValues: initialValues,
    validationSchema: schemaValidation(),
    onSubmit: (values) => handleOpenModal(values),
  });

  const getScheduleById = useCallback(async () => {
    if (!distributors.length || !subsidiaries.length) return;

    try {
      setLoading(true);
      const response = await storageService.getScheduleById(
        String(id),
        setEndpointBySlug
      );

      const responseData: State = {
        client:
          distributors.find((item) => item.id === response.customerCompanyId)
            ?.description ?? "",
        codeSubsidiary:
          subsidiaries.find((item) => item.id === response.subsidiaryId)
            ?.description ?? "",
        date: response.scheduleDate ?? "",
        time: response.scheduleTime ?? "",
        modality: response.contract.mod ?? "",
        quantity: String(response.volumeRequested) ?? "",
        note: response.customerRequesterNote ?? "",
        product: "...",
        contract: "...",
      };

      // @ts-expect-error @ts-ignore
      setValues({
        ...responseData,
        client: response.customerCompanyId,
        codeSubsidiary: response.subsidiaryId,
        product: response.contract.productId,
      });

      dispatch({ type: "set-all", value: responseData });

      setContractAndProduct({
        contractId: response.contract.id,
        productId: response.contract.productId,
      });

      setFetchContract((prevState) => ({
        ...prevState,
        customerId: response.customerCompanyId,
        subsidiaryId: response.subsidiaryId,
      }));
    } catch (error) {
      snackbar({
        message: (error as AppError).message,
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [distributors, subsidiaries]);

  const getContracts = async () => {
    try {
      const currentMonthYear = format(new Date(), "MM-yyyy");
      const threeMonthsLater = format(addMonths(new Date(), 3), "MM-yyyy");

      const response = await operationalService.getContracts(
        fetchContract.customerId,
        fetchContract.subsidiaryId,
        undefined,
        {
          monthReferenceFrom: currentMonthYear,
          monthReferenceTo: threeMonthsLater,
        }
      );

      setContracts(response);
    } catch (error) {
      snackbar({
        message: "Erro ao buscar os contratos",
        variant: "error",
      });
    }
  };

  const getProductsData = useCallback(async () => {
    const response = await getProducts?.();
    if (response?.length) {
      setProducts(response);
    }
  }, [getProducts, setProducts]);

  const getModalitiesData = useCallback(async () => {
    const response = await getModalities?.();
    if (response !== undefined) {
      setModalities(response);
    }
  }, [getModalities, setModalities]);

  const handleOpenModal = (values: typeof initialValues) => {
    setPayload(values);
    setShowConfirmation(true);
  };

  const handleClose = () => setShowConfirmation(false);

  const handleGoBack = () => {
    if (slug.includes(routesConfig.STORAGE().CREATE_SCHEDULING)) {
      navigate(routesConfig.STORAGE().RIVER_DISCHARGES);
    }

    if (slug.includes(routesConfig.STORAGE().CREATE_PUMPING_REQUEST)) {
      navigate(routesConfig.STORAGE().PIPELINES);
    }
  };

  const handleQuantityChange = (value: string) => {
    const cleanValue = value.replace(/\./g, "");
    dispatch({ type: "set-quantity", value: cleanValue || "" });
    setFieldValue("quantity", cleanValue || "");
  };

  const FIELD_OPTIONS = getFieldOptions(isDischarge, state, [
    distributors.map((item) => ({
      id: String(item.id),
      description: item.description,
    })),
    subsidiaries,
    contracts.map((item) => ({
      id: String(item.id),
      description: String(item.label),
    })),
    products,
    modalities.map((item) => ({
      id: String(item.external_id),
      description: item.description,
    })),
  ]);

  const BUTTONS: CustomButtonProps[] = [
    {
      title: "Cancelar",
      variant: CustomButtonVariant.OUTLINED,
      onClick: handleGoBack,
    },
    {
      title: "Solicitar",
      onClick: () => handleSubmit(),
    },
  ];

  useEffect(() => {
    getProductsData();
    getModalitiesData();

    if (!readyToFetch) return;

    getScheduleById();
  }, [readyToFetch]);

  useEffect(() => {
    if (fetchContract.customerId && fetchContract.subsidiaryId) {
      getContracts();
    }
  }, [fetchContract.subsidiaryId, fetchContract.customerId]);

  useEffect(() => {
    if (!isEditing) return;

    const { contractId, productId } = contractAndProduct;

    const contractLabel = contracts.find(
      (item) => item.id === contractId
    )?.label;
    const productDescription = products.find(
      (item) => item.id === productId
    )?.description;

    const fields: Array<{
      type: "set-product" | "set-contract";
      value: string;
    }> = [
      { type: "set-product", value: String(productDescription) },
      { type: "set-contract", value: String(contractLabel) },
    ];

    if (contractLabel && productDescription) {
      for (let i = 0; i < fields.length; i++) {
        dispatch({ type: fields[i].type, value: fields[i].value });
        setFieldValue(fields[i].value.split("-")[1], fields[i].value);
      }
    }
  }, [contractAndProduct, contracts, products]);

  const selectedContract = useMemo(() => {
    const found = contracts.find(({ label }) => label === state?.contract);

    return {
      mod: found?.mod ?? null,
      product: found?.product ?? null,
      productId: found?.productId ?? null,
      productMeasurementUnit: found?.productMeasurementUnit ?? "",
      monthReference: found?.monthReference,
    };
  }, [contracts, state?.contract]);

  useEffect(() => {
    const { mod, product, productId, productMeasurementUnit } =
      selectedContract;

    if (state.modality !== mod) {
      setFieldValue("modality", mod);
      dispatch({ type: "set-modality", value: mod });
    }

    if (state.product !== productId) {
      setFieldValue("product", productId);
      dispatch({ type: "set-product", value: product });
    }

    setProductMeasurementUnit(productMeasurementUnit);
  }, [
    selectedContract,
    setFieldValue,
    dispatch,
    state.modality,
    state.product,
  ]);

  return (
    <>
      {showConfirmation && (
        <RequestModal
          id={id ?? ""}
          open={showConfirmation}
          onClose={handleClose}
          data={state}
          payload={payload as typeof initialValues}
          endpoint={setEndpointBySlug}
        />
      )}

      <Layout
        enableMargin
        appBarProps={{
          notification: {
            onChange() {},
          },
          chat: {
            onChange() {},
          },
          settings: {
            onChange() {},
          },
          avatar: () => (
            <Avatar
              alt="Avatar"
              src="/images/avatar.png"
              sx={{
                width: 60,
                height: 60,
              }}
            />
          ),
        }}
      >
        <Stack sx={{ rowGap: "2rem" }}>
          <QueryPageTitleCard title={breadcrumbs[1].label as string} />

          <CustomCard sx={styles.card}>
            <CardTitleWithSubtitle
              title={breadcrumbs[2].label as string}
              buttonProps={{
                title: buttonTitle,
                sx: { textTransform: "unset" },
                onClick: handleGoBack,
              }}
            />

            <BreadcrumbCustom data={breadcrumbs} />

            <Divider sx={{ marginY: "2rem" }} />

            <Stack rowGap="2rem">
              <Stack sx={{ rowGap: "2rem", width: "30%" }}>
                <Typography sx={styles.title}>
                  Informações da programação
                </Typography>

                {FIELD_OPTIONS.slice(0, 3).map((items) => (
                  <Fragment key={items.label}>
                    {items.label !== "Filial" ? (
                      <SkeletonLoading loading={loading}>
                        <Select
                          isDisabled={
                            (items.label === "Contrato" && !contracts.length) ||
                            isEditing
                          }
                          label={items.label}
                          options={items.list.map((option) => ({
                            option,
                          }))}
                          getOptionItemLabel={(value) =>
                            value?.description ?? ""
                          }
                          getSelectedOptionLabel={(value) =>
                            value?.description ?? ""
                          }
                          onChange={(value) => {
                            // @ts-expect-error @ts-ignore
                            dispatch({
                              type: items.setState,
                              value: value?.description ?? "",
                            });
                            setFieldValue(items.field, value?.id);
                            if (items.field === "client") {
                              setFetchContract((prevState) => ({
                                ...prevState,
                                customerId: value?.id ?? "",
                              }));
                            }
                          }}
                          value={
                            items.state !== null
                              ? items.list.find(
                                  (item) => item.description === items.state
                                )
                              : null
                          }
                          // @ts-expect-error ts-ignore
                          hasError={errors[items.field]}
                          // @ts-expect-error ts-ignore
                          messageError={errors[items.field]}
                        />
                      </SkeletonLoading>
                    ) : (
                      <SkeletonLoading loading={loading}>
                        <SelectSubsidiaries
                          isDisabledValue={isEditing}
                          label={items.label}
                          valueSubsidiary={subsidiaries.find(
                            (item) => item.description === items.state
                          )}
                          onChangeValue={(selectedOption) => {
                            if (selectedOption) {
                              dispatch({
                                type: "set-code-subsidiary",
                                value: selectedOption.description,
                              });
                              setFieldValue(items.field, selectedOption.id);
                              setFetchContract((prevState) => ({
                                ...prevState,
                                subsidiaryId: selectedOption.id,
                              }));
                            }
                          }}
                          hasErrorValue={Boolean(errors.codeSubsidiary)}
                          messageErrorValue={errors.codeSubsidiary}
                        />
                      </SkeletonLoading>
                    )}
                  </Fragment>
                ))}
              </Stack>

              <Stack sx={styles.date_content}>
                <Stack width="100%">
                  <SkeletonLoading loading={loading}>
                    <CalendarSingle
                      disabledTextField={isEditing}
                      label="Data prevista (atracação)"
                      name="scheduleDate"
                      value={calendarDateByEditing}
                      onChangeDateStartEnd={(dateBegin) => {
                        const formattedDate = format(dateBegin, "yyyy-MM-dd");
                        dispatch({ type: "set-date", value: formattedDate });
                        setFieldValue("date", formattedDate);
                      }}
                      disablePastDates
                    />
                  </SkeletonLoading>
                  {errors.date && (
                    <Typography
                      sx={{
                        color: "#d32f2f",
                        margin: "4px 14px 0px",
                        fontSize: "1.2rem",
                      }}
                    >
                      {errors.date}
                    </Typography>
                  )}
                </Stack>

                <Stack alignSelf="start" width="100%">
                  <Select
                    variant={SelectVariant.STANDARD}
                    isDisabled={isEditing}
                    label="Grade de horário"
                    emptyOptionsText="Horários indisponíveis para este dia"
                    value={state.time}
                    options={
                      state.date
                        ? generateAvailableTimes(
                            new Date(state.date.replace(/-/g, "/"))
                          ).map((option) => ({ option }))
                        : []
                    }
                    getOptionItemLabel={(value) => String(value)}
                    getSelectedOptionLabel={(value) => String(value)}
                    onChange={(value) => {
                      dispatch({ type: "set-time", value });
                      setFieldValue("time", value);
                    }}
                    hasError={Boolean(errors.time)}
                    messageError={errors.time}
                  />
                </Stack>
              </Stack>

              <Stack sx={{ flexDirection: "row", gap: "1rem", width: "60%" }}>
                {FIELD_OPTIONS.slice(3).map((items) => (
                  <SkeletonLoading
                    key={items.label}
                    loading={loading}
                    width="100%"
                  >
                    <Select
                      isDisabled={isEditing}
                      label={items.label}
                      options={items.list.map((option) => ({
                        option,
                      }))}
                      getOptionItemLabel={(value) => value?.description ?? ""}
                      getSelectedOptionLabel={(value) =>
                        value?.description ?? ""
                      }
                      onChange={(value) => {
                        // @ts-expect-error @ts-ignore
                        dispatch({
                          type: items.setState,
                          value: value?.description ?? null,
                        });
                        setFieldValue(items.field, value?.id);
                      }}
                      value={
                        items.state !== null
                          ? items.list.find(
                              (item) => item.description === items.state
                            )
                          : null
                      }
                      // @ts-expect-error ts-ignore
                      hasError={errors[items.field]}
                      // @ts-expect-error ts-ignore
                      messageError={errors[items.field]}
                    />
                  </SkeletonLoading>
                ))}

                <Stack sx={{ width: "100%" }}>
                  <SkeletonLoading loading={loading}>
                    <Typography
                      fontFamily={"Open Sans"}
                      fontWeight={700}
                      color={"shadesOfDark.dark"}
                      mb={0.5}
                    >
                      Quantidade {`(${productMeasurementUnit})`}
                    </Typography>
                    <TextField
                      type="text"
                      placeholder="Informe a quantidade"
                      size="small"
                      fullWidth
                      value={applyVolumeMask(String(state.quantity || ""))}
                      onChange={({
                        target: { value },
                      }: React.ChangeEvent<HTMLInputElement>) =>
                        handleQuantityChange(value)
                      }
                      error={Boolean(errors.quantity)}
                      helperText={errors.quantity}
                    />
                  </SkeletonLoading>
                </Stack>
              </Stack>

              <Stack>
                <SkeletonLoading loading={loading} height="15rem">
                  <Typography
                    fontFamily={"Open Sans"}
                    fontWeight={700}
                    color={"shadesOfDark.dark"}
                    mb={0.5}
                  >
                    Observação
                  </Typography>

                  <Textarea
                    placeholder="Deixe sua observação"
                    value={state.note as string}
                    onChange={({
                      target: { value },
                    }: React.ChangeEvent<HTMLTextAreaElement>) =>
                      dispatch({ type: "set-note", value })
                    }
                  />
                </SkeletonLoading>
              </Stack>

              <Divider sx={{ marginX: "-2rem", borderColor: "#DFE0EB" }} />

              <Stack sx={styles.buttons_content}>
                {BUTTONS.map(({ title, variant, onClick }) => (
                  <CustomButton
                    key={title}
                    title={title}
                    variant={variant}
                    sx={{ textTransform: "unset" }}
                    onClick={onClick}
                  />
                ))}
              </Stack>
            </Stack>
          </CustomCard>
        </Stack>
      </Layout>
    </>
  );
};

const styles = {
  card: {
    boxShadow: "none",
    border: "1px solid #DFE0EB",
    height: "100%",
    padding: 2.4,
  },
  title: { fontSize: "1.5rem", fontWeight: 600, color: "#58595b" },
  date_content: {
    flexDirection: "row",
    alignItems: "center",
    gap: "1rem",
    width: "30%",
  },
  buttons_content: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: "1rem",
  },
};

export { CreateScheduleForm };
